// Event-driven dialogue manager. Wraps DeepSeekClient and gates concurrent
// requests per-AI (max 1 in-flight per plane). Distributes recent lines as
// shared context so AIs can react to one another organically.
//
// Trigger map (CLAUDE.md §4.3):
//   opening      1 per AI per match (initial intro line)
//   kill         30% chance when an AI scores a kill
//   death        100% when an AI is killed
//   ally_low     50% chance when teammate <= 1 HP
//   idle         30s of no events -> someone shoots the breeze
//   player_radio user PTT input echoed into context for AI replies

import { aiSay, aiPersonality, type SayContext, type SayResult, type BackstoryResult } from './DeepSeekClient';
import { type PersonalityId } from './personalities';

const MAX_RECENT_LINES = 10;

export interface DialogueLine {
  ts: number;
  callsign: string;
  team: 'blue' | 'red';
  personalityId: PersonalityId;
  text: string;
  source: 'live' | 'fallback';
  event: SayContext['event'];
}

export interface DialogueSubscriber {
  (line: DialogueLine): void;
}

export interface AIPlaneRef {
  callsign: string;
  team: 'blue' | 'red';
  personalityId: PersonalityId;
  alive: () => boolean;
}

export class DialogueSystem {
  recentLines: DialogueLine[] = [];
  backstories = new Map<string, BackstoryResult>();    // callsign -> backstory
  subscribers: DialogueSubscriber[] = [];
  private inFlight = new Map<string, AbortController>();
  lastEventAt = 0;

  subscribe(fn: DialogueSubscriber): () => void {
    this.subscribers.push(fn);
    return () => {
      const i = this.subscribers.indexOf(fn);
      if (i >= 0) this.subscribers.splice(i, 1);
    };
  }

  /** Pre-generate backstories for all AIs in parallel at match start. */
  async loadBackstories(planes: AIPlaneRef[]): Promise<void> {
    const otherCallsigns = planes.map((p) => p.callsign);
    const tasks = planes.map(async (p) => {
      const others = otherCallsigns.filter((c) => c !== p.callsign);
      const result = await aiPersonality(p.personalityId, others);
      this.backstories.set(p.callsign, result);
    });
    await Promise.all(tasks);
  }

  /** Fire an event for one AI; no-op if it's already mid-call. */
  async fire(plane: AIPlaneRef, event: SayContext['event'], extra?: Partial<SayContext>): Promise<void> {
    if (!plane.alive()) return;
    if (this.inFlight.has(plane.callsign)) return;

    const back = this.backstories.get(plane.callsign);
    const ctx: SayContext = {
      event,
      selfPersonality: plane.personalityId,
      selfBackstory: back,
      recentDialogue: this.recentLines.slice(-MAX_RECENT_LINES).map((l) => ({ name: back?.name ?? l.callsign, text: l.text })),
      ...extra,
    };

    const ctrl = new AbortController();
    this.inFlight.set(plane.callsign, ctrl);
    try {
      const r: SayResult = await aiSay(ctx, { signal: ctrl.signal });
      const line: DialogueLine = {
        ts: Date.now(),
        callsign: plane.callsign,
        team: plane.team,
        personalityId: plane.personalityId,
        text: r.text,
        source: r.source,
        event,
      };
      this.recentLines.push(line);
      if (this.recentLines.length > MAX_RECENT_LINES * 2) this.recentLines.shift();
      this.lastEventAt = performance.now();
      for (const s of this.subscribers) s(line);
    } finally {
      this.inFlight.delete(plane.callsign);
    }
  }

  /** Cancel all in-flight requests (e.g. on match end). */
  cancelAll(): void {
    for (const c of this.inFlight.values()) c.abort();
    this.inFlight.clear();
  }
}
